import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Calendar, Shield, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const token = sessionStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    const token = sessionStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <PageLoadingState title="Loading users" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="admin-users-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="users-title">Registered Users</h1>
          <p className="text-muted-foreground mb-12">View all registered users</p>

          {users.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {users.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="glass-morphic rounded-2xl p-6 space-y-4"
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold" data-testid={`user-name-${user.id}`}>{user.name}</p>
                        {user.is_admin && (
                          <span className="inline-flex items-center gap-1 text-xs text-accent" data-testid={`admin-badge-${user.id}`}>
                            <Shield className="h-3 w-3" />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground" data-testid={`user-email-${user.id}`}>{user.email}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground" data-testid={`user-date-${user.id}`}>
                      Joined {new Date(user.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  {!user.is_admin && (
                    <div className="pt-3 border-t border-white/10">
                      <Button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        variant="outline"
                        className="w-full text-destructive hover:bg-destructive/10"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete User
                      </Button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-users">
              <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-xl text-muted-foreground">No users yet</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
